import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context';
import { ACCOUNT_BILLING_PATH } from './accountPaths';
import BatchAddStudentsDialog from './BatchAddStudentsDialog';
import type { SchoolPayload, StudentPayload } from './checkInTypes';
import StudentDeleteConfirmDialog from './StudentDeleteConfirmDialog';
import StudentUpsertDialog from './StudentUpsertDialog';
import {
  formatStudentLimitMessage,
  getStudentLimitForPlan,
} from './studentLimit';

type StudentsPanelProps = {
  school: SchoolPayload;
  students: StudentPayload[];
  onStudentsChange: (students: StudentPayload[]) => void;
};

const PAGE_SIZE = 10;

const selectClassName =
  'rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const StudentsPanel = ({
  school,
  students,
  onStudentsChange,
}: StudentsPanelProps) => {
  const { user } = useUser();
  const [gradeFilter, setGradeFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [page, setPage] = useState(1);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [upsertMode, setUpsertMode] = useState<'add' | 'edit'>('add');
  const [editingStudent, setEditingStudent] = useState<StudentPayload | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState<StudentPayload | null>(null);
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);

  const studentLimit = getStudentLimitForPlan(user?.pricePlanName);
  const atStudentLimit =
    studentLimit !== null && students.length >= studentLimit;

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      if (gradeFilter && student.grade !== gradeFilter) return false;
      if (roomFilter && student.room !== roomFilter) return false;
      return true;
    });
  }, [gradeFilter, roomFilter, students]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
    setExpandedStudentId(null);
  }, [gradeFilter, roomFilter]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setExpandedStudentId(null);
  }, [page]);

  const pageStudents = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredStudents.slice(start, start + PAGE_SIZE);
  }, [filteredStudents, page]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">Students</h2>
        <p className="mt-1 font-sans text-sm text-ink/65">
          Filter, add, edit, or remove students for your school.
        </p>
        <p className="mt-2 font-sans text-sm text-ink/70">
          {formatStudentLimitMessage(studentLimit, students.length)}
          {studentLimit !== null && !user?.pricePlanName ? (
            <span className="text-ink/55"> Free tier limit applies until you subscribe.</span>
          ) : null}
        </p>
        {atStudentLimit ? (
          <p
            className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
            role="alert"
          >
            You&apos;ve reached your plan&apos;s student limit ({studentLimit}).{' '}
            <Link
              to={ACCOUNT_BILLING_PATH}
              className="underline decoration-ink/40 underline-offset-2 hover:decoration-ink"
            >
              Upgrade billing
            </Link>{' '}
            to add more students.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row">
          <label className="block">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Grade
            </span>
            <select
              className={`mt-1 block w-full sm:w-40 ${selectClassName}`}
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
            >
              <option value="">All grades</option>
              {school.grades.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Room
            </span>
            <select
              className={`mt-1 block w-full sm:w-40 ${selectClassName}`}
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
            >
              <option value="">All rooms</option>
              {school.rooms.map((room) => (
                <option key={room} value={room}>
                  {room}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={atStudentLimit}
            onClick={() => {
              if (atStudentLimit) return;
              setUpsertMode('add');
              setEditingStudent(null);
              setUpsertOpen(true);
            }}
            className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add student
          </button>
          <button
            type="button"
            disabled={atStudentLimit}
            onClick={() => {
              if (atStudentLimit) return;
              setBatchOpen(true);
            }}
            className="rounded-full px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/15 transition hover:bg-skysoft/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Batch add students
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border-2 border-ink/10 bg-white">
        <table className="min-w-full divide-y divide-ink/10 text-left">
          <thead className="bg-paper/70">
            <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Grade</th>
              <th className="px-4 py-3">Room</th>
              <th className="px-4 py-3">Teacher</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {pageStudents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center font-sans text-sm text-ink/55">
                  No students match the current filters.
                </td>
              </tr>
            ) : (
              pageStudents.map((student) => {
                const fullName = [student.firstName, student.middleName, student.lastName]
                  .filter(Boolean)
                  .join(' ');
                const isExpanded = expandedStudentId === student.id;
                return (
                  <Fragment key={student.id}>
                    <tr
                      className={`cursor-pointer font-sans text-sm text-ink transition hover:bg-skysoft/20 ${
                        isExpanded ? 'bg-skysoft/10' : ''
                      }`}
                      onClick={() =>
                        setExpandedStudentId((current) =>
                          current === student.id ? null : student.id,
                        )
                      }
                    >
                      <td className="px-4 py-3 font-semibold">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={`inline-block text-ink/45 transition ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                            aria-hidden
                          >
                            ▸
                          </span>
                          {fullName}
                        </span>
                      </td>
                      <td className="px-4 py-3">{student.grade || '—'}</td>
                      <td className="px-4 py-3">{student.room || '—'}</td>
                      <td className="px-4 py-3">{student.classTeacher || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div
                          className="inline-flex gap-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setUpsertMode('edit');
                              setEditingStudent(student);
                              setUpsertOpen(true);
                            }}
                            className="rounded-full px-3 py-1.5 text-sm font-semibold text-ink/75 ring-1 ring-ink/15 hover:bg-skysoft/40"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeletingStudent(student);
                              setDeleteOpen(true);
                            }}
                            className="rounded-full px-3 py-1.5 text-sm font-semibold text-doghouse ring-1 ring-doghouse/30 hover:bg-doghouse/10"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className="bg-paper/50 font-sans text-sm text-ink">
                        <td colSpan={5} className="px-4 py-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">
                            Parent / guardian
                          </p>
                          <div className="mt-3 grid gap-4 sm:grid-cols-2">
                            <div className="rounded-xl border border-ink/10 bg-white px-3 py-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">
                                Parent / guardian 1
                              </p>
                              <p className="mt-1 font-semibold">
                                {student.parent_guardian_1 || '—'}
                              </p>
                              <p className="mt-2 text-ink/70">
                                {student.parent_guardian_email_1 || '—'}
                              </p>
                              <p className="mt-1 text-ink/70">
                                {student.parent_guardian_phone_1 || '—'}
                              </p>
                            </div>
                            <div className="rounded-xl border border-ink/10 bg-white px-3 py-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">
                                Parent / guardian 2
                              </p>
                              <p className="mt-1 font-semibold">
                                {student.parent_guardian_2 || '—'}
                              </p>
                              <p className="mt-2 text-ink/70">
                                {student.parent_guardian_email_2 || '—'}
                              </p>
                              <p className="mt-1 text-ink/70">
                                {student.parent_guardian_phone_2 || '—'}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 font-sans text-sm text-ink/65">
        <p>
          {filteredStudents.length} student{filteredStudents.length === 1 ? '' : 's'}
          {filteredStudents.length > 0
            ? ` · page ${page} of ${totalPages}`
            : null}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-full px-3 py-1.5 font-semibold ring-1 ring-ink/15 hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-full px-3 py-1.5 font-semibold ring-1 ring-ink/15 hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <StudentUpsertDialog
        open={upsertOpen}
        mode={upsertMode}
        student={editingStudent}
        grades={school.grades}
        rooms={school.rooms}
        teachers={school.teachers}
        atStudentLimit={atStudentLimit}
        studentLimit={studentLimit}
        onClose={() => setUpsertOpen(false)}
        onSaved={(saved) => {
          if (upsertMode === 'edit') {
            onStudentsChange(
              students.map((item) => (item.id === saved.id ? saved : item)),
            );
          } else {
            onStudentsChange([...students, saved]);
          }
        }}
      />

      <BatchAddStudentsDialog
        open={batchOpen}
        grades={school.grades}
        rooms={school.rooms}
        teachers={school.teachers}
        remainingSlots={
          studentLimit === null ? null : Math.max(0, studentLimit - students.length)
        }
        studentLimit={studentLimit}
        onClose={() => setBatchOpen(false)}
        onCreated={(created) => {
          onStudentsChange([...students, ...created]);
        }}
      />

      <StudentDeleteConfirmDialog
        open={deleteOpen}
        student={deletingStudent}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(studentId) => {
          onStudentsChange(students.filter((item) => item.id !== studentId));
        }}
      />
    </div>
  );
};

export default StudentsPanel;
